import { useState } from 'react';

import cn from 'classnames';

import { BaseComponentProps } from '../../../types/common';
import { DotAnnotation } from '../DotAnnotation';

import styles from './Avatar.module.css';

export type AvatarVariant = 'photo' | 'team' | 'blank';

export interface AvatarProps extends BaseComponentProps {
  /** The variant of the avatar to display:
   * - 'photo': Displays a user's photo (default)
   * - 'team': Shows a team avatar with a small overlay
   * - 'blank': Shows an empty avatar with a dashed border
   */
  variant?: AvatarVariant;

  /** URL of the image to display. Used by:
   * - 'photo' variant: Shows the image as the avatar
   * - 'team' variant: Shows the image in the small overlay
   */
  src?: string;

  /**
   * When true, the avatar will fluidly adapt to its parent container size instead of using the default fixed size
   */
  fluid?: boolean;

  /**
   * The fallback text to display if the image fails to load or is not provided
   */
  fallback?: string | null;

  /**
   * The roundness of the avatar
   */
  round?: 'full' | 'square';

  /**
   * Whether to show a dot annotation on the avatar
   */
  showDotAnnotation?: boolean;
}

/**
 * Display a user's avatar image, initials, or icon. Supports different variants and can indicate online presence.
 */
export const Avatar: React.FC<AvatarProps> = ({
  variant = 'photo',
  src,
  dataTestId,
  fluid,
  round = 'full',
  fallback,
  showDotAnnotation,
}: AvatarProps) => {
  const [imageLoaded, setImageLoaded] = useState(false);

  const rootClassName = cn(
    styles.avatar,
    {
      [styles['hide-background']]:
        imageLoaded && Boolean(src) && variant !== 'team',
      [styles.fluid]: fluid,
    },
    variant === 'blank' && styles[variant],
    styles[`round-${round}`]
  );

  const handleImageLoad = () => {
    setImageLoaded(true);
  };

  return (
    <div data-avatar className={rootClassName} data-testid={dataTestId}>
      {variant === 'team' && (
        <>
          <p className={styles['no-image-text']}>{fallback}</p>
          <div className={styles.overlay}>
            <img className={styles.image} src={src} onLoad={handleImageLoad} />
          </div>
        </>
      )}

      {variant === 'photo' && src && (
        <img className={styles.image} src={src} onLoad={handleImageLoad} />
      )}

      {fallback && variant !== 'team' && variant !== 'blank' && !src && (
        <p className={styles['no-image-text']}>
          {fallback?.substring(0, 1).toLocaleUpperCase()}
        </p>
      )}

      {variant === 'blank' && <div className={styles.blank} />}

      {showDotAnnotation && <DotAnnotation />}
    </div>
  );
};
